import React, { memo, useState } from 'react';
import { Modal, List, AutoComplete, Input, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { PopAlert } from 'appkit-react';

import {
  INPUT_TYPE,
  INPUT_COMPS,
} from '@components/dragableTableFrom/formItems';
import { useDebounce } from '@components';
import Actions from '@flux/action';

const DeleteBtn = INPUT_COMPS[INPUT_TYPE.DELETE];

const UserCardInList = memo(({ guid, name, email, onRowDelete }) => (
  <List.Item className="user-card">
    <div className="user-info">
      <div className="name text-ellipsis">{name}</div>
      <div className="email text-ellipsis">{email}</div>
    </div>
    <DeleteBtn inputProps={{ onRowDelete, rowIndex: guid }} />
  </List.Item>
));
const SelectedUserList = ({ data = [], handleDelete }) =>
  !Array.isArray(data) || data.length === 0 ? (
    <div className="no-user-text">No added users yet.</div>
  ) : (
    <List className="selected-user-list">
      {data.map((user) => (
        <UserCardInList key={user.guid} {...user} onRowDelete={handleDelete} />
      ))}
    </List>
  );

const SelectCard = ({ children }) => (
  <div style={{ display: 'flex', justifyContent: 'center' }}>{children}</div>
);

const SearchInputWithDropdowm = memo(
  ({
    onSearch,
    options,
    isLoading,
    searchValue,
    onSelect,
    changeSearchValue,
    ...props
  }) => {
    const debouncedHandleSearch = useDebounce(onSearch, 1000);

    const handleSearch = (e) => {
      changeSearchValue(e);
      debouncedHandleSearch(e);
    };

    const handleSelect = (e) => {
      changeSearchValue('');
      onSelect(e);
    };

    return (
      <AutoComplete
        {...props}
        value={searchValue}
        options={options}
        onSearch={handleSearch}
        className="white-list-search-input"
        dropdownClassName="white-list-search-input-dropdown"
        notFoundContent={
          Array.isArray(options) ? (
            <SelectCard center>Not Found</SelectCard>
          ) : isLoading ? (
            <SelectCard center>
              <Spin size="small" />
            </SelectCard>
          ) : null
        }
        onSelect={handleSelect}
      >
        <Input
          className="white-list-search-input"
          size="large"
          placeholder="Search"
          prefix={<span className="appkiticon icon-search-outline" />}
        />
      </AutoComplete>
    );
  },
);

const getMemberSelectCard = (data) =>
  data?.map(({ guid, email, name, staff }, index) => ({
    value: guid,
    label: (
      <div className="white-list-user-select-card">
        <span className="name">{name}</span>
        <span>{email}</span>
        <span>{staff}</span>
      </div>
    ),
  }));

function WhiteListAddModal({ isVisible, onCancel, onOk }) {
  const dispatch = useDispatch();
  const [memberResults, setMembers] = useState();
  const [isLoading, toggleLoading] = useState(false);
  const [selectedList, setSelectedList] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const queryMember = async (e) => {
    const text = e?.trim();

    setMembers(null);

    if (!text) {
      return;
    }

    toggleLoading(true);
    const result = await dispatch(Actions.queryMemberByText(text));
    setMembers(result);

    toggleLoading(false);
  };

  const handleSelect = (guid) => {
    const selectedUser = memberResults.find((member) => member?.guid === guid);

    if (selectedUser) {
      setMembers(null);
      setSearchValue('');
      setSelectedList([
        { ...selectedUser, permission: [] },
        ...selectedList.filter((user) => user.guid !== guid),
      ]);
    }
  };

  const handleDelete = (guid) => {
    setSelectedList((currentList) =>
      currentList.filter((user) => user.guid !== guid),
    );
  };

  const handleSubmitAddeduser = async () => {
    if (selectedList.length === 0) {
      return onOk();
    }
    toggleLoading(true);

    const result = await dispatch(Actions.submitAddedUser(selectedList));
    toggleLoading(false);

    if (result?.success) {
      onOk();
      setSelectedList([]);
      setSearchValue('');

      PopAlert({
        className: 'success-message-popover',
        oneLineDisplay: true,
        duration: 2000,
        status: 'success',
        message: 'User has been added successfully.',
      });
    }
  };

  const handleCancel = () => {
    setSelectedList([]);
    setSearchValue('');
    onCancel();
    setMembers(null);
  };
  return (
    <Modal
      maskClosable={false}
      closeIcon={<span className="appkiticon icon-close-fill" />}
      wrapClassName="white-list-add-modal-wrapper"
      onCancel={handleCancel}
      className="white-list-add-modal"
      visible={isVisible}
      title={<span className="white-list-add-modal-header">Add User</span>}
      cancelButtonProps={{ className: 'cancel-btn', loading: isLoading }}
      okText="add"
      okButtonProps={{ loading: isLoading }}
      onOk={handleSubmitAddeduser}
    >
      <SearchInputWithDropdowm
        options={getMemberSelectCard(memberResults)}
        memberResults={memberResults}
        style={{ width: '100%' }}
        onSearch={queryMember}
        onSelect={handleSelect}
        isLoading={isLoading}
        searchValue={searchValue}
        changeSearchValue={setSearchValue}
      />

      <SelectedUserList data={selectedList} handleDelete={handleDelete} />
    </Modal>
  );
}

export default memo(WhiteListAddModal);
